import { nanoid } from '@reduxjs/toolkit';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import StarRating from '@/components/molecule/StarRating/StarRating';
import {
  ErrorText,
  FeedbackContainer,
  FeedbackImage,
  FormWrapper,
  ImageWrapper,
} from '@/components/organism/FeedbackForm/FeedbackForm.styles';
import { getUserProfile } from '@/configuration/login/loginSlice';
import { useSubmitFeedbackMutation } from '@/services/feedbackApi/feedbackApi';
import { Feedback } from '@/store/feedback/feedbackSlice';
import { getUserId } from '@/utils/getUserId';

import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { SuccessMessage } from '../SuccessMessage/SuccessMessage';

const classes = {
  container: 'display-flex flex-column',
  title: 'margin-0',
  formGroup: 'form-group',
  formControl: 'form-control',
  button: 'btn btn-primary',
  buttonContainer: 'button-container',
};

const texts = {
  title: 'feedbackPage.feedbackForm.title',
  appeal: 'feedbackPage.feedbackForm.appeal',
  questionOneLabel: 'feedbackPage.feedbackForm.question_one.label',
  questionTwoLabel: 'feedbackPage.feedbackForm.question_two.label',
  additionalCommentLabel: 'feedbackPage.feedbackForm.additional_comment.label',
  commentPlaceholder: 'common.feedback.comment.placeholder',
  submittingLabel: 'feedbackPage.feedbackForm.submiting.label',
  submitLabel: 'common.feedback.submit.label',
  successNotification: 'common.feedback.success.notification',
  errorNotification: 'common.feedback.error.notification',
  validationEmptyError: 'common.feedback.validation.empty.error',
  validationTooLongError: 'common.feedback.validation.tooLong.error',
};

type FeedbackDataProps = {
  rate: number | 0;
  improvementSuggestions: string;
  additionalComments: string;
};

const FeedbackForm = () => {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<FeedbackDataProps>({ mode: 'onChange' });
  const intl = useIntl();

  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [formKey, setFormKey] = useState(0);

  const user = useSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');

  const handleSuccessDialogClose = () => setShowSuccessDialog(false);
  const handleErrorDialogClose = () => setShowErrorDialog(false);

  const validateInput = (value: string) => {
    const specialCharacterRegex = /^[^{}<>]*$/;
    if (!specialCharacterRegex.test(value)) {
      return intl.formatMessage({ id: texts.validationEmptyError });
    }
    return true;
  };

  const onSubmit = async (data: FeedbackDataProps) => {
    try {
      const detail =
        data.improvementSuggestions || data.additionalComments
          ? `${data.improvementSuggestions} ${data.additionalComments}`
          : '';

      const feedbackData: Feedback = {
        id: nanoid(),
        rate: data.rate,
        detail,
        userId,
      };

      const response = await submitFeedback(feedbackData);

      if ('data' in response && response.data) {
        setShowSuccessDialog(true);
        reset();
        setFormKey((prev) => prev + 1);
      } else {
        setRequestError(
          `${intl.formatMessage({ id: texts.errorNotification })} ${
            (response as { error?: { error?: string } }).error?.error ?? ''
          }`,
        );
        setShowErrorDialog(true);
      }
    } catch (e: unknown) {
      setRequestError(e instanceof Error ? e.message : String(e));
      setShowErrorDialog(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && isValid) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <div className={classes.container}>
      <h3 className={classes.title}>
        <FormattedMessage id={texts.title} />
      </h3>
      <p>
        <FormattedMessage id={texts.appeal} />
      </p>
      <FeedbackContainer>
        <FormWrapper>
          <form data-testid="feedback-form" onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
            <div className={classes.formGroup}>
              <label>
                <FormattedMessage id={texts.questionOneLabel} />
                <Controller
                  name="rate"
                  control={control}
                  key={formKey}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <StarRating {...field} onChange={(rating) => field.onChange(rating)} data-testid="star-rating" />
                  )}
                />
              </label>
            </div>

            <div className={classes.formGroup}>
              <label>
                <FormattedMessage id={texts.questionTwoLabel} />
                <textarea
                  className={classes.formControl}
                  spellCheck="false"
                  rows={6}
                  placeholder={intl.formatMessage({ id: texts.commentPlaceholder })}
                  {...register('improvementSuggestions', { validate: validateInput })}
                  maxLength={2000}
                  data-testid="improvementSuggestions"
                />
              </label>
              {errors.improvementSuggestions && <ErrorText>{errors.improvementSuggestions.message}</ErrorText>}
            </div>

            <div className={classes.formGroup}>
              <label style={{ width: '100%' }}>
                <FormattedMessage id={texts.additionalCommentLabel} />
                <textarea
                  className={classes.formControl}
                  spellCheck="false"
                  rows={6}
                  placeholder={intl.formatMessage({ id: texts.commentPlaceholder })}
                  {...register('additionalComments', { validate: validateInput })}
                  maxLength={2000}
                  data-testid="additionalComments"
                />
              </label>
              {errors.additionalComments && <ErrorText>{errors.additionalComments.message}</ErrorText>}
            </div>

            <div className={classes.buttonContainer}>
              <button
                type="submit"
                disabled={isLoading || !isValid}
                className={classes.button}
                data-testid="feedback-submit"
              >
                <FormattedMessage id={isLoading ? texts.submittingLabel : texts.submitLabel} />
              </button>
            </div>
          </form>
        </FormWrapper>

        <ImageWrapper>
          <FeedbackImage src="assets/images/feedback.svg" alt="Feedback" />
        </ImageWrapper>

        {showSuccessDialog && (
          <SuccessMessage
            show={showSuccessDialog}
            handleClose={handleSuccessDialogClose}
            message={texts.successNotification}
          />
        )}
        {showErrorDialog && (
          <ErrorMessage show={showErrorDialog} handleClose={handleErrorDialogClose} message={requestError} />
        )}
      </FeedbackContainer>
    </div>
  );
};

export default FeedbackForm;
